import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { ToastService } from 'src/app/shared/services/toast.service';
import { OrganizationService } from '../../services/organization.service';
import { Organization } from '../../models/organization';
import { OrganizationUpdate } from '../../models/org-update';
import { IncadaTenant } from '../../models/incada-tenant';
import { LaunchDarklyService } from 'src/app/shared/services/launch-darkly.service';
import { FleetManagementService } from 'src/app/internal-user/fleet-management/services/fleet-management.service';
import { Subscription } from 'rxjs';
import * as constants from '../../../../config/app-constants';
import { GenericConfirmationDialogComponent } from 'src/app/shared/generic-confirmation-dialog/generic-confirmation-dialog.component';

export interface EditOrganizationDialogData {
  organization: Organization;
}

@Component({
    selector: 'app-edit-organization-dialog',
    templateUrl: './edit-organization-dialog.component.html',
    styleUrls: ['./edit-organization-dialog.component.scss'],
    standalone: false
})
export class EditOrganizationDialogComponent implements OnInit {
  get orgName(): string { return this.formGroup.get('name').value; }

  organization: Organization;
  formGroup: FormGroup;
  showIncadaOption = false;
  enableIncada = false;
  fieldsLocked = false;
  incadaEnabled = false;
  deviceCheckComplete = false;
  lockTooltip = 'Edits are locked; Stratus has at least one device provisioned for this organization.';
  private selectedTenant: IncadaTenant | null = null;
  private flagSubscription: Subscription;

  constructor(
    public dialogRef: MatDialogRef<EditOrganizationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditOrganizationDialogData,
    private readonly organizationService: OrganizationService,
    private readonly ngxLoader: NgxUiLoaderService,
    private readonly toastService: ToastService,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly fleetManagementService: FleetManagementService,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.organization = this.data.organization;
    this.formGroup = new FormGroup({
      name: new FormControl(this.organization.name),
    });

    this.flagSubscription = this.launchDarklyService.flagChange.subscribe((flags) => {
      this.showIncadaOption = flags['release-incada'] ?? false;
    });

    // Pre-populate if org already has Incada enabled
    if (this.organization.incadaId) {
      this.enableIncada = true;
      this.selectedTenant = {
        id: this.organization.incadaId,
        slug: this.organization.incadaSlug,
        name: this.organization.incadaName,
        region: this.organization.incadaRegion,
        description: '',
        status: '',
      };
      this.formGroup.addControl(
        'incadaTenant',
        new FormControl(this.organization.incadaName, [Validators.required])
      );
    }

    this.checkProvisionedDevices();
  }

  private checkProvisionedDevices() {
    this.fleetManagementService.searchEdgeDevices({
      orgId: this.organization.customerNo,
      enabled: true,
      provisioned: true,
    }).subscribe({
      next: (devices) => {
        if (devices && devices.length > 0) {
          this.fieldsLocked = true;
          this.formGroup.get('name').disable();

          if (this.organization.incadaId) {
            this.incadaEnabled = true;
          }
        }
        this.deviceCheckComplete = true;
      },
      error: (response) => {
        if (response?.status === 404) {
          // No devices found — fields stay unlocked
          this.deviceCheckComplete = true;
        }
      },
    });
  }

  ngOnDestroy() {
    if (this.flagSubscription) {
      this.flagSubscription.unsubscribe();
    }
  }

  onToggleIncada() {
    if (this.enableIncada) {
      this.formGroup.addControl('incadaTenant', new FormControl('', [Validators.required]));
      this.selectedTenant = null;
    } else {
      this.formGroup.removeControl('incadaTenant');
      this.selectedTenant = null;
    }
  }

  onTenantSelected(tenant: IncadaTenant | null) {
    this.selectedTenant = tenant;
  }

  closePopup(result?: string) {
    this.dialogRef.close(result);
  }

  save() {
    if (!this.deviceCheckComplete || !this.formGroup.valid) {
      return;
    }

    if (this.hasIncadaTenantChangedFromExisting()) {
      const dialogRef = this.dialog.open(GenericConfirmationDialogComponent, {
        width: '520px',
        maxWidth: '95vw',
        disableClose: true,
      });

      const instance = dialogRef.componentInstance;
      instance.dialogRef = dialogRef;
      instance.title = 'Change Incada Tenant';
      instance.message = 'Changing this organization\'s Incada tenant will clear Incada mapping data on any child sites. This action cannot be undone. Continue?';

      dialogRef.afterClosed().subscribe((userConfirmed) => {
        if (userConfirmed === true) {
          this.submitOrganizationUpdate();
        }
      });

      return;
    }

    this.submitOrganizationUpdate();
  }

  private hasIncadaTenantChangedFromExisting(): boolean {
    if (!this.organization?.incadaId) {
      return false;
    }

    return this.organization.incadaId !== (this.selectedTenant?.id ?? null);
  }

  private submitOrganizationUpdate(): void {
    const orgUpdate: OrganizationUpdate = {
      name: this.orgName,
      incadaId: this.selectedTenant?.id ?? null,
      incadaSlug: this.selectedTenant?.slug ?? null,
      incadaName: this.selectedTenant?.name ?? null,
      incadaRegion: this.selectedTenant?.region ?? null
    };

    this.ngxLoader.start('edit-organization-submit');

    this.organizationService.updateOrg(this.organization.organizationId, orgUpdate)
      .subscribe({
        next: () => {
          this.ngxLoader.stop('edit-organization-submit');
          this.closePopup('success');
          setTimeout(() => {
            this.toastService.openToast('Organization updated.', constants.ToastPanelType.done);
          }, 700);
        },
        error: (response) => {
          this.toastService.stopLoadingAndShowError(response, 'Error updating organization.', 'edit-organization-submit');
        },
      });
  }
}
